import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import ReactJson from 'react18-json-view';
import { strings } from '/imports/strings';
import { Dict } from '/imports/types';
import { css } from '/imports/ui/css';
import { formatDate, formatDateTime, uniqueArray } from '/imports/utils';
import { Input } from '/imports/ui/Input';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useUser } from '/imports/hooks';
import { Catalog } from '/imports/ui/Catalog';
import { toastOnError } from '/imports/ui/utils';
import { listLogs, ListLogsArg, Log, logData, LogLevel } from '/imports/api/logs';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';

const PAGE_SIZE = 200;
const MAX_LIMIT = 2000;
const POLL_MS = 10000;
const DEBOUNCE_MS = 350;

// One place for a level's look: the filter pill, the row badge and the detail panel's rule all read it.
const LEVELS: { key: LogLevel; label: () => string; color: string }[] = [
  { key: 'trace', label: strings.Trace, color: css.grey4 },
  { key: 'info', label: strings.Info, color: css.primaryColor },
  { key: 'warning', label: strings.Warning, color: css.orange },
  { key: 'error', label: strings.Error, color: css.red2 },
  { key: 'critical', label: strings.Critical, color: css.red1 },
];
const levelVars = LEVELS.map((l) => `&[data-level="${l.key}"] { --level: ${l.color}; }`).join('\n');

const LogsPageStyled = styled(Page)`
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; }
  .catalog-tools {
    flex-wrap: wrap;
    .search { flex: 1 1 16rem; }
    .tags { flex: 0 1 14rem; }
    .filters { gap: 0.5rem; flex-wrap: wrap; }
  }
  .log-list {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: auto;
    transition: opacity 0.15s ease;
    &.stale { opacity: 0.6; }
  }
  .top-sentinel { height: 1px; }
  .new-entries {
    position: sticky;
    top: 0.5rem;
    z-index: 2;
    display: block;
    margin: 0 auto 0.5rem;
    font: inherit;
    font-size: 0.85rem;
    color: ${css.white};
    background: ${css.primaryColor};
    border: none;
    border-radius: 1rem;
    padding: 0.35rem 1rem;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    &:hover { background: ${css.primaryColor2}; }
  }
  .empty { padding: 3rem 1rem; text-align: center; color: ${css.grey4}; }
  .catalog-stats {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    .spacer { flex: 1; }
    .live { color: ${css.green2}; }
    .paused { color: ${css.orange}; }
  }
`;

const LevelPillStyled = styled.button`
  ${levelVars}
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid var(--level);
  color: var(--level);
  background: ${css.white};
  border-radius: 1rem;
  padding: 0.35rem 0.9rem;
  min-width: 5rem;
  transition: background 0.15s ease, color 0.15s ease;
  &.enabled { background: var(--level); color: ${css.white}; }
  &:focus-visible { outline: 2px solid ${css.primaryColor}; outline-offset: 2px; }
`;

const RowStyled = styled.div`
  ${levelVars}
  border-bottom: 1px solid ${css.grey7};
  .head {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    padding: 0.45rem 0.5rem;
    cursor: pointer;
    line-height: 1.4;
    &:hover { background: ${css.grey9}; }
    &:focus-visible { outline: 2px solid ${css.primaryColor}; outline-offset: -2px; }
  }
  &.expanded .head { background: ${css.grey8}; }
  .chevron { flex: none; align-self: center; }
  .badge {
    flex: none;
    width: 4.6rem;
    text-align: center;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--level);
    border: 1px solid var(--level);
    border-radius: 0.6rem;
    padding: 0.05rem 0.4rem;
  }
  &[data-level="critical"] .badge { background: var(--level); color: ${css.white}; }
  .time {
    flex: none;
    font-variant-numeric: tabular-nums;
    color: ${css.grey3};
    font-size: 0.85rem;
    white-space: nowrap;
    .date { color: ${css.grey4}; margin-left: 0.4rem; }
  }
  .message { flex: 1 1 16rem; min-width: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
  .detail {
    margin: 0 0.5rem 0.75rem 1.9rem;
    padding: 0.5rem 0.75rem;
    border-left: 3px solid var(--level);
    background: ${css.grey9};
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    font-size: 0.9rem;
    .label {
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${css.grey4};
      margin-bottom: 0.25rem;
    }
    .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .chip {
      font: inherit;
      font-size: 0.8rem;
      border: 1px solid ${css.grey5};
      background: ${css.white};
      border-radius: 1rem;
      padding: 0.05rem 0.6rem;
      cursor: pointer;
      color: ${css.grey2};
      &:hover { border-color: ${css.primaryColor}; color: ${css.primaryColor}; }
    }
    .muted { color: ${css.grey4}; }
    code { font-size: 0.85rem; }
  }
`;

interface Detail { loading: boolean; data?: unknown }

/**
 * The server's latest page, keeping the object already held for any entry that is still present. Rows
 * are memoised on that object, so an unchanged entry does not re-render on a poll — which is most of
 * what made the page jitter. Returns `prev` itself when nothing changed, so React skips the render too.
 */
const merge = (prev: Log[], next: Log[]): Log[] => {
  const byId = new Map(prev.map((l) => [l._id, l]));
  let same = prev.length === next.length;
  const out = next.map((l, i) => {
    const kept = byId.get(l._id);
    if (!kept || prev[i] !== kept) same = false;
    return kept || l;
  });
  return same ? prev : out;
};

const splitTags = (text: string) => uniqueArray(text.split(',').map((t) => t.trim()).filter(Boolean));

/** Page listing server logs with level, tag and message filters, live polling and expandable detail. */
export const LogsPage = () => {
  const user = useUser();
  const [levelFilters, setLevelFilters] = useState<Set<LogLevel>>(() => new Set(['warning', 'error', 'critical']));
  const [tagsText, setTagsText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [arg, setArg] = useState<ListLogsArg>(() => ({ limit: PAGE_SIZE, levels: ['warning', 'error', 'critical'] }));
  const [logs, setLogs] = useState<Log[]>([]);
  const [initializing, setInitializing] = useState(true);
  const [stale, setStale] = useState(false);            // a filter change is in flight; the old list is shown dimmed
  const [paused, setPaused] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date>();
  // A poll result held back because inserting it would move what the reader is looking at.
  const [pending, setPending] = useState<Log[]>();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [details, setDetails] = useState<Dict<Detail>>({});
  const [atTop, setAtTop] = useState(true);
  const topRef = useRef<HTMLDivElement>(null);
  const requestedDetails = useRef(new Set<string>());
  // The poll decides whether to insert or hold back from the latest values, not the ones it closed over.
  const holdBack = useRef(false);
  holdBack.current = expanded.size > 0 || !atTop;
  const loadSeq = useRef(0);

  // Typing fires a request per keystroke otherwise, each response replacing the list.
  useEffect(() => {
    const timer = setTimeout(() => {
      const tags = splitTags(tagsText);
      const search = searchText.trim();
      setArg({ limit, levels: [...levelFilters], tags: tags.length ? tags : undefined, search: search || undefined });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [tagsText, searchText, levelFilters, limit]);

  /**
   * Fetches the current filter's latest page. 'replace' (a filter change, Refresh, Show more) always lands;
   * 'poll' lands only when the reader is at the top with nothing open, and is otherwise held as pending.
   * A sequence guard stops a slow early response overwriting a fast later one.
   */
  const load = useCallback(async (mode: 'replace' | 'poll', a: ListLogsArg) => {
    const seq = ++loadSeq.current;
    if (mode === 'replace') setStale(true);
    try {
      const result = await listLogs.call(a);
      if (seq !== loadSeq.current) return;
      setUpdatedAt(new Date());
      if (mode === 'poll' && holdBack.current) {
        setPending(result);
        return;
      }
      setPending(undefined);
      setLogs((prev) => merge(prev, result));
    }
    catch (error: any) {
      // A failed poll keeps what is on screen and says nothing; a failed explicit load says why.
      if (mode === 'replace') toast.error(`${error?.reason || error?.message || error}`);
    }
    finally {
      if (seq === loadSeq.current) {
        setStale(false);
        setInitializing(false);
      }
    }
  }, []);

  useEffect(() => {
    load('replace', arg);
    if (paused) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;   // nobody is looking; save the request
      load('poll', arg);
    }, POLL_MS);
    // Coming back to the tab: catch up at once rather than at the next tick.
    const onVisible = () => { if (document.visibilityState === 'visible') load('poll', arg); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [arg, paused, load]);

  // Whether the top of the list is in view. New entries insert at the top, so when it is scrolled away
  // they would land unseen and shift everything below — they wait as pending instead.
  useEffect(() => {
    const el = topRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => setAtTop(entry.isIntersecting), { rootMargin: '48px 0px 0px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pendingCount = useMemo(() => {
    if (!pending) return 0;
    const shown = new Set(logs.map((l) => l._id));
    return pending.filter((l) => !shown.has(l._id)).length;
  }, [pending, logs]);

  const applyPending = () => {
    if (!pending) return;
    const next = merge(logs, pending);
    const ids = new Set(next.map((l) => l._id));
    setLogs(next);
    setPending(undefined);
    setExpanded((prev) => new Set([...prev].filter((id) => ids.has(id))));
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  /** The entry's detail payload is fetched once, on first open, and kept for the life of the page. */
  const fetchDetail = toastOnError(async (id: string) => {
    try {
      const data = await logData.call(id);
      setDetails((prev) => ({ ...prev, [id]: { loading: false, data } }));
    }
    catch (error) {
      setDetails((prev) => ({ ...prev, [id]: { loading: false } }));
      requestedDetails.current.delete(id);
      throw error;
    }
  });
  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (requestedDetails.current.has(id)) return;
    requestedDetails.current.add(id);
    setDetails((prev) => ({ ...prev, [id]: { loading: true } }));
    fetchDetail(id);
  }, []);

  const addTag = useCallback((tag: string) => {
    setTagsText((text) => {
      const tags = splitTags(text);
      return tags.includes(tag) ? text : [...tags, tag].join(', ');
    });
  }, []);

  const toggleLevel = (level: LogLevel) => setLevelFilters((prev) => {
    const next = new Set(prev);
    if (next.has(level)) next.delete(level);
    else next.add(level);
    return next;
  });

  // listLogs is super-user-only and refuses everyone else, so the page could only ever render its own
  // chrome with an empty list — which read as a reachable admin screen.
  if (user && !user.isSuperUser) return <NotPermittedPage />;

  return (
    <LogsPageStyled className="page">
      <PageHeading
        left={<h1>{strings.Logs()}</h1>}
        right={
          <div className="heading-buttons">
            <Button className="secondary compact" onClick={() => setPaused((p) => !p)}>{paused ? strings.Resume() : strings.Pause()}</Button>
            <Button className="secondary compact" onClick={() => load('replace', arg)}>{strings.Refresh()}</Button>
          </div>
        }
      />
      <PageBody>
        <Catalog>
          <div className="catalog-tools">
            <div className="search"><Input icon="search" placeholder={strings.SearchMessages()} value={searchText} onChange={(v) => setSearchText(v || '')} /></div>
            <div className="tags"><Input placeholder={strings.Tags()} value={tagsText} onChange={(v) => setTagsText(v || '')} /></div>
            <div className="filters">
              {
                LEVELS.map((l) => (
                  <LevelPillStyled
                    key={l.key}
                    type="button"
                    data-level={l.key}
                    className={levelFilters.has(l.key) ? 'enabled' : undefined}
                    aria-pressed={levelFilters.has(l.key)}
                    onClick={() => toggleLevel(l.key)}
                  >
                    {l.label()}
                  </LevelPillStyled>
                ))
              }
            </div>
          </div>
          <div className={`log-list${stale ? ' stale' : ''}`}>
            <div ref={topRef} className="top-sentinel" />
            {pendingCount > 0 && <button type="button" className="new-entries" onClick={applyPending}>{strings.NewLogEntries(pendingCount)}</button>}
            {
              initializing ? <LoadingFill />
                : !logs.length ? <div className="empty">{strings.NoLogEntries()}</div>
                  : logs.map((log) => (
                    <LogRow key={log._id} log={log} expanded={expanded.has(log._id)} detail={details[log._id]} onToggle={toggle} onTag={addTag} />
                  ))
            }
          </div>
          <div className="catalog-stats">
            <span>{strings.LogEntries(logs.length)}</span>
            <span>·</span>
            <span className={paused ? 'paused' : 'live'}>{paused ? strings.Paused() : strings.Live()}</span>
            {updatedAt && <><span>·</span><span>{strings.Updated()} {formatDateTime(updatedAt, 'HH:mm:ss')}</span></>}
            <span className="spacer" />
            {
              logs.length >= limit && limit < MAX_LIMIT &&
              <Button className="secondary compact" onClick={() => setLimit((n) => Math.min(MAX_LIMIT, n + PAGE_SIZE))}>{strings.ShowMore()}</Button>
            }
          </div>
        </Catalog>
      </PageBody>
    </LogsPageStyled>
  );
};

const isObject = (v: unknown): v is object => typeof v === 'object' && v !== null;

/** One entry: level badge, time and message; opens to its tags, data payload and remaining metadata. */
const LogRow = React.memo(function LogRow(props: {
  log: Log;
  expanded: boolean;
  detail?: Detail;
  onToggle: (id: string) => void;
  onTag: (tag: string) => void;
}) {
  const { log, expanded, detail, onToggle, onTag } = props;
  const level = LEVELS.find((l) => l.key === log.level);
  const tags: string[] = Array.isArray(log.extra?.tags) ? log.extra.tags : [];
  // Everything the entry carries besides the message and the tags, which have their own place.
  const rest = useMemo(() => {
    const out: Dict<unknown> = { ...(log.extra || {}) };
    delete out.tags;
    if (log.orgId) out.orgId = log.orgId;
    if (log.clientTimestamp) out.clientTimestamp = log.clientTimestamp;
    return Object.keys(out).length ? out : undefined;
  }, [log]);
  const open = () => onToggle(log._id);
  return (
    <RowStyled data-level={log.level} className={expanded ? 'expanded' : undefined}>
      <div
        className="head"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      >
        <Icon className="chevron" icon={expanded ? 'expandMore' : 'chevronRight'} size={18} fill={css.grey4} stroke={css.grey4} />
        <span className="badge">{level?.label() || log.level}</span>
        <span className="time">
          {formatDateTime(log.timestamp, 'HH:mm:ss')}
          <span className="date">{formatDate(log.timestamp)}</span>
        </span>
        <span className="message">{log.message}</span>
      </div>
      {
        expanded &&
        <div className="detail">
          {
            tags.length > 0 &&
            <div>
              <div className="label">{strings.Tags()}</div>
              <div className="chips">
                {tags.map((t) => <button key={t} type="button" className="chip" onClick={() => onTag(t)}>{t}</button>)}
              </div>
            </div>
          }
          <div>
            <div className="label">{strings.Data()}</div>
            {
              !detail || detail.loading ? <span className="muted">…</span>
                : detail.data === undefined ? <span className="muted">—</span>
                  : isObject(detail.data) ? <ReactJson src={detail.data} collapsed={2} displaySize />
                    : <code>{String(detail.data)}</code>
            }
          </div>
          {
            rest &&
            <div>
              <div className="label">{strings.Details()}</div>
              <ReactJson src={rest} collapsed={1} />
            </div>
          }
        </div>
      }
    </RowStyled>
  );
});
